import type {
  TAutoReplyMessageAsset,
  TCardTypeMessagePayload,
  TRichMessagePayload,
} from 'api';
import { findRichMessageTemplate } from 'constants/richMessageTemplates';
import { TRichMessageTemplate } from 'types/richMessage';
import { parseRichMessagePayload, resolveImagemapBaseUrl } from './richMessage';

/**
 * プレビュー描画に必要な素材フィールドだけを表す型。
 * 集計画面のように素材APIを経由しない画面が organizationId 等をダミー値で
 * 埋めずに済むよう、preview系の入力はこの型で受ける。
 */
export type TPreviewMessageAsset = Pick<
  TAutoReplyMessageAsset,
  'id' | 'type' | 'name' | 'payload' | 'previewImageUrl'
>;

export function getRichMessagePreviewImageUrl(
  asset: TPreviewMessageAsset,
): string | null {
  const preview = asset.previewImageUrl?.trim();
  if (preview) return preview;

  const payload = asset.payload as TRichMessagePayload;
  const base = resolveImagemapBaseUrl(payload);
  if (base) return `${base}/1040`;

  return payload.imageUrl?.trim() || null;
}

export function getRichMessagePreviewTemplate(
  asset: TPreviewMessageAsset,
  templates?: TRichMessageTemplate[],
): TRichMessageTemplate | undefined {
  const parsed = parseRichMessagePayload(asset.payload, templates);
  const templateId = parsed?.templateId;

  if (!templateId) return undefined;

  return findRichMessageTemplate(templates, templateId);
}

export function getCardTypePreviewImageUrl(
  asset: TPreviewMessageAsset,
): string | null {
  const preview = asset.previewImageUrl?.trim();
  if (preview) return preview;

  const payload = asset.payload as TCardTypeMessagePayload;
  const firstCard = payload.cards[0];

  return firstCard?.imageUrl?.trim() || null;
}

export function findAssetById<T extends TPreviewMessageAsset>(
  assets: T[] | undefined,
  assetId: number | undefined,
): T | undefined {
  if (assetId == null || !assets?.length) return undefined;
  const id = Number(assetId);
  if (Number.isNaN(id)) return undefined;

  return assets.find((asset) => asset.id === id);
}
